import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CollectionVersionSummary } from "../../../../api/content/contentDecoders";
import { listCorpusVersions } from "../../../../api/content/corporaApi";
import { listWorkflows } from "../../../../api/content/workflowsApi";
import type { ThreadEntry } from "../../../../api/threads/liveMessages";
import type { ThreadOverview } from "../../../../api/threads/threadDecoders";
import { ENTRIES_PAGE_LIMIT, readEntries, readForkPoint, readThread, type ForkChoice } from "../../../../api/threads/threadsApi";
import ErrorNotice from "../../../../errors/ErrorNotice";
import { useLoad } from "../../../../shared/loading/useLoad";
import { useSubmission } from "../../../../shared/state/useSubmission";
import AppButton from "../../../../ui/buttons/AppButton";
import LoadStatus from "../../../../ui/feedback/LoadStatus";
import Field from "../../../../ui/forms/Field";
import OptionLabel from "../../../../ui/forms/OptionLabel";
import { FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../../../ui/styles/formStyles";
import { nameOfStep, NO_STEP_NAMES, stepNamesOf } from "../../data/stepNames";
import { useThreadProcess } from "../../data/useThreadProcess";
import { labelOf, useVersionLabels } from "../../../versions/labels/useVersionLabels";
import { versionChoiceText } from "../../../versions/labels/versionChoiceText";
import ForkPreviewList from "../../hosting/ForkPreviewList";
import { boundCorpusId, changesVersions, forkOntoNewest, otherCorpusVersion, type ForkVersions } from "../../hosting/forkVersions";
import type { PreparationViewProps } from "../preparationView";
import { startFork } from "../startCommands";
import type { ForkIntent } from "../threadIntent";
import { branchPointsOf } from "./branchPoints";
import { forkOntoQuestions, holdsReviewedAnnotations } from "./questionsFork";

/** At most this many entries are read to offer branch points; a longer Thread still forks at its cited entry. */
const MAXIMUM_ENTRY_PAGES = 10;

/** The Thread and its entries up to the fork, to offer the steps a branch can start before. */
interface ForkSource {
  readonly overview: ThreadOverview;
  readonly entries: readonly ThreadEntry[];
}

async function readForkSource(threadId: string, signal: AbortSignal): Promise<ForkSource> {
  const overview = await readThread(threadId, signal);
  const entries: ThreadEntry[] = [];
  for (let page = 0; page < MAXIMUM_ENTRY_PAGES && entries.length < overview.lastEntryNumber; page += 1) {
    const read = await readEntries(threadId, entries.at(-1)?.number ?? 0, ENTRIES_PAGE_LIMIT, signal);
    if (read.entries.length === 0) break;
    entries.push(...read.entries);
  }
  return { overview, entries };
}

/**
 * Branching a Thread (T-10, SB-22, F30): a new Thread with the history up to the branch
 * point, hosted by the user and saved until they attach a connector; nothing runs at a fork.
 * The branch point is an entry, or the place just before a step's newest attempt ("Branch
 * from a step…", R-7). The fork may run on the newest content and Workflow versions, and on
 * another version of its corpus. At a reviewed annotation set it may instead go on with
 * "Draft competency questions" from those entries (D-05). The server's fork preview says what
 * is kept and what runs.
 */
export default function ForkPreparation({ intent, requestKey, onStarted }: PreparationViewProps<ForkIntent>) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => readForkSource(intent.threadId, signal), [intent.threadId]);
  const { state: source, reload } = useLoad(load);
  if (source.status !== "loaded") return <LoadStatus state={source} loadingLabel={t("threads.fork.loadingPoint")} onRetry={reload} />;
  return <ForkSetup intent={intent} requestKey={requestKey} onStarted={onStarted} source={source.value} />;
}

function ForkSetup({ intent, requestKey, onStarted, source }: PreparationViewProps<ForkIntent> & { source: ForkSource }) {
  const { t, i18n } = useTranslation();
  const process = useThreadProcess(source.overview.workflow, intent.projectId);
  const names = process.state.status === "loaded" && process.state.value !== null ? stepNamesOf(process.state.value, i18n.resolvedLanguage) : NO_STEP_NAMES;
  const points = useMemo(() => branchPointsOf(source.entries).filter((point) => point.atEntry < intent.atEntry), [source.entries, intent.atEntry]);
  const [atEntry, setAtEntry] = useState(intent.atEntry);
  // What a fork at this entry inherits, not the Thread's latest bindings (RF-04).
  const loadPoint = useCallback((signal: AbortSignal) => readForkPoint(intent.threadId, atEntry, signal), [intent.threadId, atEntry]);
  const { state: point, reload } = useLoad(loadPoint);

  return (
    <div className="space-y-4">
      <p className={FORM_HELP_TEXT_CLASS}>{t("threads.fork.explanation")}</p>
      <Field label={t("threads.prepare.fork.point")} hint={t("threads.prepare.fork.pointHint")}>
        {(control) => (
          <select {...control} className={FORM_SELECT_CLASS} value={atEntry} onChange={(event) => { setAtEntry(Number(event.target.value)); }}>
            <option value={intent.atEntry}>{t("threads.prepare.fork.atEntry", { entry: intent.atEntry })}</option>
            {points.map((branch) => (
              <option key={branch.stepKey} value={branch.atEntry}>
                {t("threads.prepare.fork.beforeStep", { step: nameOfStep(names, branch.stepKey), entry: branch.atEntry })}
              </option>
            ))}
          </select>
        )}
      </Field>
      <LoadStatus state={point} loadingLabel={t("threads.fork.loadingPoint")} onRetry={reload} />
      {point.status === "loaded" ? (
        <ForkChoices
          key={atEntry}
          intent={intent}
          requestKey={requestKey}
          onStarted={onStarted}
          title={source.overview.title}
          atEntry={atEntry}
          current={point.value}
          questionsFromHere={holdsReviewedAnnotations(source.entries.find((entry) => entry.number === atEntry))}
        />
      ) : null}
    </div>
  );
}

interface ForkChoicesProps extends PreparationViewProps<ForkIntent> {
  title: string;
  atEntry: number;
  /** The bindings and Workflow at the fork point. */
  current: ForkVersions;
  /** Whether the fork point is a reviewed annotation set, which questions can be drafted from. */
  questionsFromHere: boolean;
}

function ForkChoices({ intent, requestKey, onStarted, title: threadTitle, atEntry, current, questionsFromHere }: ForkChoicesProps) {
  const { t, i18n } = useTranslation();
  const { threadId, projectId } = intent;
  const [onNewest, setOnNewest] = useState(false);
  const [toQuestions, setToQuestions] = useState(false);
  const title = toQuestions ? t("threads.fork.questionsTitle", { title: threadTitle }) : t("threads.fork.defaultTitle", { title: threadTitle, entry: atEntry });
  const { state, submit } = useSubmission();
  const loadLatest = useCallback((signal: AbortSignal) => listWorkflows(projectId, null, signal), [projectId]);
  const { state: latest } = useLoad(loadLatest);
  const newest = useMemo(() => (latest.status === "loaded" ? forkOntoNewest(current, latest.value) : null), [latest, current]);
  const newerExists = newest !== null && changesVersions(current, newest);
  const questions = useMemo(() => (questionsFromHere && latest.status === "loaded" ? forkOntoQuestions(current, latest.value) : null), [questionsFromHere, latest, current]);
  const corpusId = boundCorpusId(current);
  const loadCorpusVersions = useCallback(
    (signal: AbortSignal): Promise<readonly CollectionVersionSummary[]> => (corpusId === null ? Promise.resolve([]) : listCorpusVersions(corpusId, signal)),
    [corpusId],
  );
  const { state: corpusVersions } = useLoad(loadCorpusVersions);
  const ownCorpusVersion = current.bindings.corpus_version;
  const [corpusVersion, setCorpusVersion] = useState(ownCorpusVersion);
  // Corpus versions by their labels (ADR-0033 D1): `features/versions/` names them.
  const corpusLabels = useVersionLabels(projectId, { itemVersions: [], collectionVersions: corpusVersions.status === "loaded" ? corpusVersions.value.map((version) => version.reference) : [] });
  const corpusVersionLabel = (version: CollectionVersionSummary, index: number) =>
    versionChoiceText(
      t,
      t("versions.corpus"),
      labelOf(corpusLabels, version.reference),
      version,
      [...(index === 0 ? [t("threads.prepare.run.newest")] : []), ...(version.reference === ownCorpusVersion ? [t("threads.fork.atThisEntry")] : [])],
      i18n.resolvedLanguage,
    );
  const ownCorpusVersionListed = corpusVersions.status !== "loaded" || ownCorpusVersion === null || corpusVersions.value.some((version) => version.reference === ownCorpusVersion);
  // One request for the preview and the fork, so what is shown is what is made (D37).
  const choice = useMemo((): ForkChoice => {
    const onto = toQuestions && questions !== null ? questions : onNewest && newest !== null ? newest : undefined;
    const onCorpus = otherCorpusVersion(current, corpusVersion);
    return { atEntry, ...(onto === undefined ? {} : { onto }), ...(onCorpus === undefined ? {} : { corpusVersion: onCorpus }) };
  }, [atEntry, toQuestions, questions, onNewest, newest, current, corpusVersion]);
  const starting = state.status === "submitting" || state.status === "succeeded";

  const fork = () => {
    submit(async () => {
      const created = await startFork({ ...choice, threadId, title, requestKey });
      onStarted(created.threadId);
    });
  };

  return (
    <div className="space-y-4">
      {questions !== null ? (
        <OptionLabel align="start">
          <input type="checkbox" className="mt-1" checked={toQuestions} disabled={starting} onChange={(event) => { setToQuestions(event.target.checked); }} />
          <span>
            <span className="block">{t("threads.fork.toQuestions")}</span>
            <span className={FORM_HELP_TEXT_CLASS}>{t("threads.fork.toQuestionsDetail")}</span>
          </span>
        </OptionLabel>
      ) : null}
      {newerExists && !toQuestions ? (
        <OptionLabel align="start">
          <input type="checkbox" className="mt-1" checked={onNewest} disabled={starting} onChange={(event) => { setOnNewest(event.target.checked); }} />
          <span>
            <span className="block">{t("threads.fork.onNewest")}</span>
            <span className={FORM_HELP_TEXT_CLASS}>{t("threads.fork.onNewestDetail")}</span>
          </span>
        </OptionLabel>
      ) : null}
      {corpusVersions.status === "loaded" && corpusVersions.value.length > 1 ? (
        <Field label={t("threads.fork.corpusVersion")} hint={t("threads.fork.corpusVersionDetail")}>
          {(control) => (
            <select {...control} className={FORM_SELECT_CLASS} value={corpusVersion ?? ""} disabled={starting} onChange={(event) => { setCorpusVersion(event.target.value); }}>
              {corpusVersions.value.map((version, index) => (
                <option key={version.reference} value={version.reference}>{corpusVersionLabel(version, index)}</option>
              ))}
              {ownCorpusVersionListed ? null : <option value={ownCorpusVersion}>{t("threads.fork.atThisEntry")}</option>}
            </select>
          )}
        </Field>
      ) : null}
      <ForkPreviewList threadId={threadId} projectId={projectId} choice={choice} />
      {latest.status === "failed" ? <ErrorNotice error={latest.error} compact={true} /> : null}
      {corpusVersions.status === "failed" ? <ErrorNotice error={corpusVersions.error} compact={true} /> : null}
      <p className={FORM_HELP_TEXT_CLASS}>{t("threads.prepare.run.title", { title })}</p>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      <div className="flex justify-end">
        <AppButton variant="primary" size="sm" onClick={fork} disabled={starting}>{starting ? t("threads.prepare.starting") : t("threads.fork.submit")}</AppButton>
      </div>
    </div>
  );
}
